import { createFileRoute, Link } from "@tanstack/react-router"
import { useState } from "react"
import {
	IconCheck,
	IconX,
	IconClock,
	IconEye,
} from "@tabler/icons-react"
import { AppShell } from "@/components/layout/app-shell"
import { ProtectedRoute } from "@/components/layout/protected-route"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { useMerchantOrders } from "@/hooks/use-merchant-orders"
import { ORDER_STATUS } from "@/lib/constants"
import type { Order } from "@/types/api"

export const Route = createFileRoute("/orders")({
	component: OrdersPage,
})

function OrdersPage() {
	return (
		<ProtectedRoute>
			<AppShell>
				<OrdersContent />
			</AppShell>
		</ProtectedRoute>
	)
}

const statusConfig: Record<
	string,
	{ label: string; icon: typeof IconCheck; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
	completed: { label: "Completed", icon: IconCheck, variant: "default" },
	pending: { label: "Pending", icon: IconClock, variant: "secondary" },
	processing: { label: "Processing", icon: IconClock, variant: "outline" },
	failed: { label: "Failed", icon: IconX, variant: "destructive" },
	expired: { label: "Expired", icon: IconX, variant: "destructive" },
	cancelled: { label: "Cancelled", icon: IconX, variant: "outline" },
}

function OrdersContent() {
	const [statusFilter, setStatusFilter] = useState<string>("all")
	const [searchQuery, setSearchQuery] = useState("")

	const { data: ordersData, isLoading, error } = useMerchantOrders(
		statusFilter !== "all" ? { status: statusFilter as Order["status"] } : {}
	)

	const orders = ordersData?.data || []
	const pagination = ordersData?.pagination

	const filteredOrders = orders.filter((order) =>
		searchQuery
			? order.merchantOrderId.toLowerCase().includes(searchQuery.toLowerCase()) ||
				order.playerId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
				order.simPhoneNumber?.includes(searchQuery)
			: true
	)

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold tracking-tight">Orders</h1>
					<p className="text-muted-foreground">
						View and manage all your deposit and payout orders
					</p>
				</div>
			</div>

			{/* Filters */}
			<Card>
				<CardHeader>
					<CardTitle>Filters</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex flex-wrap gap-4">
						<div className="flex-1 min-w-[200px]">
							<Input
								placeholder="Search by order ID, player ID or phone..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
						</div>
						<div className="w-[180px]">
							<Select value={statusFilter} onValueChange={setStatusFilter}>
								<SelectTrigger>
									<SelectValue placeholder="Status" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All Statuses</SelectItem>
									<SelectItem value={ORDER_STATUS.PENDING}>Pending</SelectItem>
									<SelectItem value={ORDER_STATUS.PROCESSING}>Processing</SelectItem>
									<SelectItem value={ORDER_STATUS.COMPLETED}>Completed</SelectItem>
									<SelectItem value={ORDER_STATUS.FAILED}>Failed</SelectItem>
									<SelectItem value={ORDER_STATUS.EXPIRED}>Expired</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Orders Table */}
			<Card>
				<CardHeader>
					<CardTitle>Order History</CardTitle>
					<CardDescription>
						{pagination?.total || 0} total orders
					</CardDescription>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="flex items-center justify-center h-64">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
						</div>
					) : error ? (
						<div className="flex items-center justify-center h-64">
							<p className="text-destructive">Failed to load orders</p>
						</div>
					) : filteredOrders.length === 0 ? (
						<div className="flex items-center justify-center h-64">
							<p className="text-muted-foreground">No orders found</p>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Order ID</TableHead>
									<TableHead>Type</TableHead>
									<TableHead>Amount</TableHead>
									<TableHead>Channel</TableHead>
									<TableHead>Player ID</TableHead>
									<TableHead>SIM Number</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Created</TableHead>
									<TableHead className="text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{filteredOrders.map((order) => {
									const config = statusConfig[order.status] || statusConfig.pending
									const StatusIcon = config.icon
									return (
										<TableRow key={order.id}>
											<TableCell className="font-medium">
												{order.merchantOrderId}
											</TableCell>
											<TableCell>
												<Badge variant="outline">
													{order.orderType === "collect" ? "Deposit" : "Payout"}
												</Badge>
											</TableCell>
											<TableCell>৳{Number(order.amount).toLocaleString()}</TableCell>
											<TableCell>{order.channel}</TableCell>
											<TableCell>{order.playerId || "-"}</TableCell>
											<TableCell>{order.simPhoneNumber || "-"}</TableCell>
											<TableCell>
												<Badge variant={config.variant}>
													<StatusIcon className="h-3 w-3 mr-1" />
													{config.label}
												</Badge>
											</TableCell>
											<TableCell>
												{new Date(order.createdAt).toLocaleDateString()}
											</TableCell>
											<TableCell className="text-right">
												<Button
													variant="ghost"
													size="sm"
													asChild
												>
													<Link to="/orders/$orderId" params={{ orderId: order.id }}>
														<IconEye className="h-4 w-4" />
													</Link>
												</Button>
											</TableCell>
										</TableRow>
									)
								})}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>
		</div>
	)
}
